import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { ListSkeleton } from "@/components/PageSkeleton";
import {
  ShieldAlert,
  Shield,
  MessageSquare,
  Send,
  ChevronRight,
  Lock,
  Save,
} from "lucide-react";
import {
  RaiseConcernMessage,
  RaiseConcernCaseHR,
  RaiseConcernStatus,
  apiGetAllConcernCases,
  apiUpdateConcernStatus,
  apiUpdateConcernNotes,
  apiUpdateConcernFeedback,
  getTenantSlug,
} from "@/lib/api";

// ── Constants ─────────────────────────────────────────────────────────────────

const HR_CASE_STATUS_OPTIONS = [
  "submitted",
  "in_review",
  "assigned",
  "resolved",
  "closed",
] as const;

const OPEN_CASE_STATUSES: RaiseConcernStatus[] = [
  "submitted",
  "in_review",
  "assigned",
];

const CASE_STATUS_CONFIG: Record<RaiseConcernStatus, { label: string; color: string }> = {
  submitted: {
    label: "status_submitted",
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  },
  in_review: {
    label: "status_in_review",
    color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
  },
  assigned: {
    label: "status_assigned",
    color: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  },
  resolved: {
    label: "status_resolved",
    color: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  },
  closed: {
    label: "status_closed",
    color: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  },
  cancelled: {
    label: "status_cancelled",
    color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  },
};

function isConcernOpenStatus(status: RaiseConcernStatus) {
  return OPEN_CASE_STATUSES.includes(status);
}

function parseConcernMessages(raw: string | null): RaiseConcernMessage[] {
  if (!raw?.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch {
    return [];
  }
  return [];
}

// ── Chat bubble renderer ───────────────────────────────────────────────────────

function ConcernThread({ rawMessages }: { rawMessages: string | null }) {
  const { t } = useTranslation();
  const bottomRef = useRef<HTMLDivElement>(null);
  const messages = parseConcernMessages(rawMessages);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [rawMessages]);

  const getLabel = (message: RaiseConcernMessage) => {
    if (message.kind === "case_cancelled")
      return t("raiseConcern.caseCancelledHistory", { defaultValue: "Concern cancelled" });
    if (message.kind === "review_requested")
      return t("raiseConcern.reviewRequestedHistory", { defaultValue: "Review requested" });
    if (message.sender === "submitter")
      return t("raiseConcern.anonymousSubmitter", { defaultValue: "Anonymous Submitter" });
    return t("raiseConcern.youHr", { defaultValue: "You (HR)" });
  };

  if (messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full py-8">
        <p className="text-xs text-muted-foreground text-center">
          {t("raiseConcern.noConversationYet", { defaultValue: "No messages yet." })}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {messages.map((message) => {
        const isActionMessage =
          message.kind === "case_cancelled" || message.kind === "review_requested";

        if (isActionMessage) {
          return (
            <div
              key={message.id}
              className="mx-auto max-w-[90%] rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-2 dark:border-amber-800 dark:bg-amber-950/20 text-center"
            >
              <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                {getLabel(message)}
              </p>
              {message.text && (
                <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-200 whitespace-pre-wrap">
                  {message.text}
                </p>
              )}
              <span className="mt-1 block text-[10px] text-amber-600/70 dark:text-amber-300/60">
                {new Date(message.timestamp).toLocaleString()}
              </span>
            </div>
          );
        }

        const isMe = message.sender === "hr";

        return (
          <div
            key={message.id}
            className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}
          >
            <div
              className={`px-3.5 py-2.5 text-sm max-w-[82%] shadow-sm ${
                isMe
                  ? "bg-primary text-primary-foreground rounded-2xl rounded-tr-sm"
                  : "bg-background border rounded-2xl rounded-tl-sm"
              }`}
            >
              <p className="whitespace-pre-wrap leading-relaxed">{message.text}</p>
            </div>
            <span className="text-[10px] text-muted-foreground mt-1 px-1.5">
              {getLabel(message)} · {new Date(message.timestamp).toLocaleString()}
            </span>
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function HrCases() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const isAdminOrHr =
    user?.role?.mainRole === "hr" || user?.role?.mainRole === "admin";

  // ── HR: all cases ────────────────────────────────────────────────────────────
  const { data: allCases = [], isLoading: hrCasesLoading } = useQuery<RaiseConcernCaseHR[]>({
    queryKey: ["all-concern-cases", getTenantSlug()],
    queryFn: () => apiGetAllConcernCases(),
    enabled: isAdminOrHr,
  });

  const [selectedHrCase, setSelectedHrCase] = useState<RaiseConcernCaseHR | null>(null);
  const [hrCaseDetailOpen, setHrCaseDetailOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<RaiseConcernStatus>("submitted");
  const [feedbackText, setFeedbackText] = useState("");
  const [hrNotesDraft, setHrNotesDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [sendingHrMessage, setSendingHrMessage] = useState(false);
  type CaseFilter = "all" | "open" | "closed" | RaiseConcernStatus;
  const [caseFilter, setCaseFilter] = useState<CaseFilter>(() => {
    const f = searchParams.get("filter");
    const validStatuses: RaiseConcernStatus[] = ["submitted", "in_review", "assigned", "resolved", "closed", "cancelled"];
    if (f === "open" || f === "closed") return f;
    if (validStatuses.includes(f as RaiseConcernStatus)) return f as RaiseConcernStatus;
    return "all";
  });

  const openHrCaseDetail = (c: RaiseConcernCaseHR) => {
    setSelectedHrCase(c);
    setNewStatus(c.status);
    setHrNotesDraft(c.hrNotes ?? "");
    setFeedbackText("");
    setHrCaseDetailOpen(true);
  };

  const handleHrSendMessage = async () => {
    if (!selectedHrCase || !feedbackText.trim()) return;
    setSendingHrMessage(true);
    try {
      await apiUpdateConcernFeedback(selectedHrCase.id, feedbackText.trim());
      setFeedbackText("");
      toast({ title: t("raiseConcern.feedbackSavedDesc", { defaultValue: "Message sent to submitter." }) });
      await qc.invalidateQueries({ queryKey: ["all-concern-cases", getTenantSlug()] });
      const refreshed = (await qc.fetchQuery({
        queryKey: ["all-concern-cases", getTenantSlug()],
        queryFn: () => apiGetAllConcernCases(),
      })) as RaiseConcernCaseHR[];
      const updated = refreshed.find((c) => c.id === selectedHrCase.id);
      if (updated) setSelectedHrCase(updated);
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSendingHrMessage(false);
    }
  };

  const handleSaveStatus = async () => {
    if (!selectedHrCase) return;
    if (newStatus === selectedHrCase.status) return;
    setSavingStatus(true);
    try {
      await apiUpdateConcernStatus(selectedHrCase.id, newStatus);
      toast({ title: t("raiseConcern.statusChanged", { defaultValue: "Status updated." }) });
      setSelectedHrCase({ ...selectedHrCase, status: newStatus as any });
      await qc.invalidateQueries({ queryKey: ["all-concern-cases", getTenantSlug()] });
      await qc.invalidateQueries({ queryKey: ["concern-open-count"] });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSavingStatus(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedHrCase) return;
    setSavingNote(true);
    try {
      await apiUpdateConcernNotes(selectedHrCase.id, hrNotesDraft);
      toast({ title: t("raiseConcern.noteSaved") });
      qc.invalidateQueries({ queryKey: ["all-concern-cases", getTenantSlug()] });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSavingNote(false);
    }
  };

  const filteredHrCases = allCases.filter((c) => {
    if (caseFilter === "open") return isConcernOpenStatus(c.status);
    if (caseFilter === "closed") return !isConcernOpenStatus(c.status);
    if (caseFilter !== "all") return c.status === caseFilter;
    return true;
  });

  // Auto-open HR case from notification link (?caseId=N)
  useEffect(() => {
    if (!isAdminOrHr) return;
    const caseIdParam = searchParams.get("caseId");
    if (caseIdParam && allCases.length > 0) {
      const target = allCases.find((c) => c.id === Number(caseIdParam));
      if (target) {
        openHrCaseDetail(target);
        setSearchParams(
          (prev) => { const next = new URLSearchParams(prev); next.delete("caseId"); return next; },
          { replace: true }
        );
      }
    }
  }, [searchParams, allCases, isAdminOrHr, setSearchParams]);

  const openCasesCount = allCases.filter((c) => isConcernOpenStatus(c.status)).length;

  // ── Main render ──────────────────────────────────────────────────────────────

  return (
    <AppLayout>
      <div className="space-y-6 w-full max-w-3xl mx-auto">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-orange-500/10 flex items-center justify-center shrink-0">
              <ShieldAlert className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{t("raiseConcern.hrCasesTitle", { defaultValue: "HR Open Cases" })}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {openCasesCount > 0
                  ? t("raiseConcern.hrCasesSubtitle", { count: openCasesCount, defaultValue: `${openCasesCount} open case${openCasesCount !== 1 ? "s" : ""} requiring attention` })
                  : t("raiseConcern.hrCasesSubtitleEmpty", { defaultValue: "All concern cases are reviewed." })}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2.5 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-300">
          <Shield className="h-4 w-4 shrink-0" />
          <p>{t("raiseConcern.identityProtectedNotice", { defaultValue: "Submitter identities are always hidden. HR cannot see who raised any concern." })}</p>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2">
          {(["all", "open", "closed"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setCaseFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                caseFilter === f ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {t(`raiseConcern.filter${f.charAt(0).toUpperCase() + f.slice(1)}`, { defaultValue: f })}
              {f === "all" && ` (${allCases.length})`}
              {f === "open" && ` (${allCases.filter((c) => isConcernOpenStatus(c.status)).length})`}
              {f === "closed" && ` (${allCases.filter((c) => !isConcernOpenStatus(c.status)).length})`}
            </button>
          ))}
          {/* Status-specific filter pill — shown when drilled in from the dashboard stat boxes */}
          {!["all", "open", "closed"].includes(caseFilter) && (() => {
            const cfg = CASE_STATUS_CONFIG[caseFilter as RaiseConcernStatus];
            return (
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border ${cfg?.color ?? ""}`}>
                {t(`raiseConcern.${cfg?.label}`, { defaultValue: caseFilter })}
                <span className="font-normal opacity-70">({filteredHrCases.length})</span>
                <button
                  onClick={() => setCaseFilter("all")}
                  className="ml-0.5 hover:opacity-70 transition-opacity"
                  title="Clear filter"
                >
                  ×
                </button>
              </span>
            );
          })()}
        </div>

        {/* Case list */}
        <div className="space-y-4">
          {hrCasesLoading && <ListSkeleton rows={4} />}
          {!hrCasesLoading && filteredHrCases.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center text-muted-foreground">
                <ShieldAlert className="h-12 w-12 mx-auto mb-4 opacity-40" />
                <p className="font-medium">{t("raiseConcern.hrCasesTitle", { defaultValue: "No cases match this filter" })}</p>
              </CardContent>
            </Card>
          )}
          {!hrCasesLoading && filteredHrCases.map((c) => {
            const cfg = CASE_STATUS_CONFIG[c.status] ?? CASE_STATUS_CONFIG.submitted;
            return (
              <Card
                key={c.id}
                className="hover:shadow-md transition-all cursor-pointer group"
                onClick={() => openHrCaseDetail(c)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs font-mono text-muted-foreground">{t("raiseConcern.caseId", { id: c.id })}</span>
                        <Badge className={`text-xs ${cfg.color}`}>{t(`raiseConcern.${cfg.label}`)}</Badge>
                        {c.hrFeedback && (
                          <Badge variant="outline" className="text-xs gap-1 text-blue-600">
                            <MessageSquare className="h-3 w-3" />
                            {t("raiseConcern.hasUpdates", { defaultValue: "Has updates" })}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm font-semibold">
                        {c.reportedEmployeeName}
                        {c.reportedEmployeeTitle && <span className="font-normal text-muted-foreground"> · {c.reportedEmployeeTitle}</span>}
                        {(c as RaiseConcernCaseHR).reportedEmployeeDept && <span className="font-normal text-muted-foreground"> · {(c as RaiseConcernCaseHR).reportedEmployeeDept}</span>}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.description}</p>
                      {(() => {
                        if (!c.hrFeedback) return null;
                        try {
                          const arr = JSON.parse(c.hrFeedback);
                          if (Array.isArray(arr) && arr.length > 0) {
                            const last = arr[arr.length - 1];
                            const isHrMsg = last.sender === "hr";
                            const isAction = last.kind === "case_cancelled" || last.kind === "review_requested";
                            return (
                              <div className={`mt-2.5 p-2 rounded-md border ${isAction ? "bg-amber-50/70 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800" : isHrMsg ? "bg-blue-50/50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800" : "bg-muted/50 border-muted"}`}>
                                <span className={`text-xs font-semibold block mb-0.5 ${isAction ? "text-amber-700 dark:text-amber-300" : isHrMsg ? "text-blue-700 dark:text-blue-400" : "text-muted-foreground"}`}>
                                  {isAction ? t("raiseConcern.latestAction", { defaultValue: "Latest case action:" }) : isHrMsg ? t("raiseConcern.latestHrMessage", { defaultValue: "Last message from HR:" }) : t("raiseConcern.latestSubmitterMessage", { defaultValue: "Last reply from Submitter:" })}
                                </span>
                                <span className="text-sm text-muted-foreground line-clamp-2">{last.text}</span>
                              </div>
                            );
                          }
                        } catch { }
                        return null;
                      })()}
                    </div>
                    <div className="text-right shrink-0 space-y-1">
                      <p className="text-xs text-muted-foreground">{new Date(c.createdAt).toLocaleDateString()}</p>
                      {c.assignedToName && <p className="text-xs text-muted-foreground">{t("raiseConcern.assignedTo", { defaultValue: "Assigned" })}: {c.assignedToName}</p>}
                      <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* ── HR Case Detail Dialog ── */}
      <Dialog open={hrCaseDetailOpen} onOpenChange={(v) => { if (!v) { setHrCaseDetailOpen(false); setSelectedHrCase(null); } }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col overflow-hidden p-0">
          <DialogHeader className="px-6 py-4 border-b shrink-0 bg-muted/20">
            <div className="flex items-center gap-3 pr-10">
              <DialogTitle className="flex items-center gap-2 flex-1 min-w-0">
                <ShieldAlert className="h-5 w-5 text-orange-500 shrink-0" />
                <span className="truncate">
                  {t("raiseConcern.caseDetail", { defaultValue: "Case Details" })} —{" "}
                  {selectedHrCase && t("raiseConcern.caseId", { id: selectedHrCase.id, defaultValue: `Case #${selectedHrCase.id}` })}
                </span>
              </DialogTitle>
              {selectedHrCase && (
                <Badge className={`text-xs shrink-0 ${(CASE_STATUS_CONFIG[selectedHrCase.status] ?? CASE_STATUS_CONFIG.submitted).color}`}>
                  {t(`raiseConcern.${(CASE_STATUS_CONFIG[selectedHrCase.status] ?? CASE_STATUS_CONFIG.submitted).label}`)}
                </Badge>
              )}
            </div>
          </DialogHeader>

          {selectedHrCase && (() => {
            return (
              <div className="flex-1 overflow-hidden flex flex-col md:flex-row min-h-0">

                {/* ── LEFT: Case info + conversation ── */}
                <div className="flex-1 flex flex-col overflow-hidden min-h-0 md:border-r">
                  {/* Case meta + description */}
                  <div className="px-6 py-4 space-y-3 border-b shrink-0">
                    <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-300">
                      <Shield className="h-3.5 w-3.5 shrink-0" />
                      {t("raiseConcern.identityProtectedNotice", { defaultValue: "Submitter identity is strictly protected. It is never displayed in this interface." })}
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                      <div>
                        <span className="text-xs text-muted-foreground">{t("raiseConcern.reported", { defaultValue: "Reported" })}</span>
                        <p className="font-semibold">{selectedHrCase.reportedEmployeeName}</p>
                        {selectedHrCase.reportedEmployeeTitle && <p className="text-xs text-muted-foreground">{selectedHrCase.reportedEmployeeTitle}</p>}
                        {(selectedHrCase as RaiseConcernCaseHR).reportedEmployeeDept && <p className="text-xs text-muted-foreground">{(selectedHrCase as RaiseConcernCaseHR).reportedEmployeeDept}</p>}
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground">{t("raiseConcern.submittedOn", { defaultValue: "Submitted" })}</span>
                        <p className="font-medium">{new Date(selectedHrCase.createdAt).toLocaleDateString()}</p>
                        <p className="text-xs text-muted-foreground">{t("raiseConcern.lastUpdated", { defaultValue: "Last updated" })}: {new Date(selectedHrCase.updatedAt).toLocaleDateString()}</p>
                        {selectedHrCase.assignedToName && <p className="text-xs text-muted-foreground mt-1">{t("raiseConcern.assignedTo", { defaultValue: "Assigned" })}: {selectedHrCase.assignedToName}</p>}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">{t("raiseConcern.description", { defaultValue: "Description" })}</Label>
                      <p className="mt-1 text-sm bg-muted/30 rounded-lg px-3 py-2 whitespace-pre-wrap">{selectedHrCase.description}</p>
                    </div>
                  </div>

                  {/* Conversation thread */}
                  <div className="px-5 py-2.5 border-b shrink-0 flex items-center gap-1.5 bg-muted/30">
                    <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm font-semibold">{t("raiseConcern.conversationThread", { defaultValue: "Conversation Thread" })}</span>
                  </div>
                  <div className="flex-1 overflow-y-auto px-5 py-4 bg-slate-50 dark:bg-slate-900/30 min-h-0">
                    <ConcernThread rawMessages={selectedHrCase.hrFeedback} />
                  </div>

                  {/* Message composer */}
                  <div className="px-5 py-4 border-t bg-background shrink-0 space-y-2">
                    <p className="text-xs text-muted-foreground">
                      {t("raiseConcern.hrReplyHint", { defaultValue: "Send a secure, anonymous reply back to the submitter." })}
                    </p>
                    <Textarea
                      className="min-h-[72px] resize-none"
                      placeholder={t("raiseConcern.hrReplyPlaceholder", { defaultValue: "Type your message or request for more information here..." })}
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleHrSendMessage(); }}
                    />
                    <div className="flex justify-end">
                      <Button size="sm" onClick={handleHrSendMessage} disabled={sendingHrMessage || !feedbackText.trim()}>
                        <Send className="h-3.5 w-3.5 mr-1.5" />
                        {sendingHrMessage ? t("common.saving", { defaultValue: "Sending..." }) : t("raiseConcern.sendMessage", { defaultValue: "Send Message" })}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* ── RIGHT: Status + Notes ── */}
                <div className="w-full md:w-72 lg:w-80 shrink-0 overflow-y-auto p-5 space-y-5 border-t md:border-t-0 bg-muted/10">
                  {/* Status */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold">{t("raiseConcern.changeStatus", { defaultValue: "Change Status" })}</h4>
                    <div className="flex flex-wrap gap-2">
                      {HR_CASE_STATUS_OPTIONS.map((s) => {
                        const c2 = CASE_STATUS_CONFIG[s];
                        return (
                          <button
                            key={s}
                            onClick={() => setNewStatus(s)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border-2 ${
                              newStatus === s
                                ? "border-primary bg-primary/10"
                                : "border-transparent bg-background hover:border-primary/30"
                            } ${c2.color}`}
                          >
                            {t(`raiseConcern.${c2.label}`, { defaultValue: c2.label })}
                          </button>
                        );
                      })}
                    </div>
                    <Button
                      size="sm"
                      onClick={handleSaveStatus}
                      disabled={savingStatus || newStatus === selectedHrCase.status}
                      className="w-full"
                    >
                      <Save className="h-3.5 w-3.5 mr-1.5" />
                      {savingStatus ? t("raiseConcern.savingNote", { defaultValue: "Saving..." }) : t("raiseConcern.changeStatus", { defaultValue: "Change Status" })}
                    </Button>
                  </div>

                  {/* Internal Notes */}
                  <div className="rounded-xl border border-amber-200 dark:border-amber-800 overflow-hidden">
                    <div className="flex items-center gap-2 px-3.5 py-2.5 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800">
                      <Lock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
                      <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                        {t("raiseConcern.hrNotesLabel", { defaultValue: "Internal Notes" })}
                      </span>
                      <Badge className="ml-auto text-[10px] py-0 px-1.5 bg-amber-100 text-amber-700 border border-amber-300 dark:bg-amber-900/50 dark:text-amber-300 dark:border-amber-700">
                        {t("raiseConcern.hrOnly", { defaultValue: "HR only" })}
                      </Badge>
                    </div>
                    <div className="p-3.5 space-y-3 bg-white dark:bg-card">
                      <p className="text-xs text-muted-foreground">
                        {t("raiseConcern.hrNotesPrivate", { defaultValue: "Private — never shown to the submitter." })}
                      </p>
                      <Textarea
                        className="min-h-[100px] resize-none text-sm"
                        placeholder={t("raiseConcern.hrNotesPlaceholder", { defaultValue: "Internal discussion, investigation notes..." })}
                        value={hrNotesDraft}
                        onChange={(e) => setHrNotesDraft(e.target.value)}
                      />
                      <Button size="sm" variant="outline" onClick={handleSaveNotes} disabled={savingNote} className="w-full">
                        <Save className="h-3.5 w-3.5 mr-1.5" />
                        {savingNote ? t("raiseConcern.savingNote", { defaultValue: "Saving..." }) : t("raiseConcern.addNote", { defaultValue: "Save Internal Note" })}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
